import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, GRAFFITI_HERO, POTTERY_IMAGE, OWNER_SPACE_IMAGE, NEON_IMAGE, SNACKS_IMAGE } from "./api";

export const DEFAULTS = {
  about_kicker: "About MAS",
  hero_badge: "Appointments only · Everyone welcome",
  hero_title_1: "MAS Art",
  hero_title_2: "Workshops",
  hero_subtitle:
    "An underground art lounge in downtown Sheboygan — concrete walls, huge art, deep couches, and a paintbrush waiting for you.",
  hero_meta: "817 New York Ave · Snacks & drinks included · No experience needed",
  hero_image: GRAFFITI_HERO,
  schedule_kicker: "the lineup",
  schedule_heading: "Upcoming Workshops",
  gallery_kicker: "from the lounge",
  gallery_heading: "The Gallery",
  videos_kicker: "inside the studio",
  videos_heading: "See the lounge in motion.",
  faq_kicker: "quick answers",
  faq_heading: "Questions? We've got you.",
  faq_1_q: "Do I need any experience?",
  faq_1_a: "None at all. Every session is hosted step by step, start to finish — most of our guests haven't painted since grade school. You'll leave with something you're proud of, promise.",
  faq_2_q: "What's included with my ticket?",
  faq_2_a: "Everything. Your canvas, paints, brushes and apron are all set up before you arrive — plus snacks and non-alcoholic drinks are on the house. Just bring yourself.",
  faq_3_q: "Can I bring kids or a group?",
  faq_3_a: "Yes — all ages are welcome (under 16 with an adult). For groups, just book seats together in the same timeslot. Planning something big, like a birthday or team night? Call or text (920) 377-5900.",
  faq_4_q: "How do I get in on the night?",
  faq_4_a: "We're at 817 New York Ave in downtown Sheboygan. Doors open 15 minutes before your timeslot — show your booking code at the door, grab a snack, and pick your seat.",
  lounge_kicker: "the lounge",
  lounge_heading: "Concrete walls, high ceilings, and the best kind of night out.",
  space_title: "Couches, coffee tables & canvas.",
  space_text:
    "Sink into the lounge before your session — plants on the windowsills, original pieces on the walls, and whatever underground record we're feeling that night.",
  neon_title: "Art everywhere you look.",
  neon_text: "Huge original pieces and neon on the walls — the room itself is part of the show.",
  snacks_title: "Snacks & sips on the house.",
  snacks_text: "Every ticket includes snacks and non-alcoholic drinks. Come hungry, leave happy.",
  lounge_space_image: OWNER_SPACE_IMAGE,
  lounge_art_image: NEON_IMAGE,
  lounge_snacks_image: SNACKS_IMAGE,
  feature_1_title: "Snacks & sips included",
  feature_1_text: "Every ticket comes with snacks and non-alcoholic drinks on the house.",
  feature_2_title: "Zero experience",
  feature_2_text: "Step-by-step hosting all night. You cannot mess this up.",
  feature_3_title: "Music worth Shazam-ing",
  feature_3_text: "Underground, niche, always on — the playlist is half the reason to come.",
  feature_4_title: "Bring anyone",
  feature_4_text: "Dates, friends, family, coworkers — all ages and all skill levels fit right in.",
  pottery_kicker: "into clay?",
  pottery_heading: "Pottery has its own home.",
  pottery_text:
    "Wheel throwing, glazing and clay hand-building live over at our sister studio. Same downtown Sheboygan address, same everything-included promise.",
  pottery_button: "Visit MAS Pottery",
  pottery_image: POTTERY_IMAGE,
  visit_heading: "817 New York Ave,\nDowntown Sheboygan.",
  visit_text:
    "Sessions are booked ahead only — lock in your seat online and we'll have your easel, apron, snacks and paints ready when you walk in.",
  policies_kicker: "good to know",
  policies_heading: "House Policies",
  policy_1_title: "All sales are final",
  policy_1_text:
    "No refunds — but life happens. Text us at (920) 377-5900 at least 24 hours before your session and we'll move you to a future night, on the house.",
  policy_2_title: "Everyone's welcome",
  policy_2_text:
    "All ages and all skill levels fit right in. Under 16? Bring an adult along and paint side by side.",
  policy_3_title: "Arrive 10 minutes early",
  policy_3_text:
    "Doors open 15 minutes before your timeslot. Grab a snack, pick your seat, settle in.",
  policy_4_title: "Dress for mess",
  policy_4_text:
    "Aprons are provided, but paint has a mind of its own — leave the dry-clean-only fits at home.",
  terms_heading: "Terms & Conditions",
  prepare_title: "How to prepare",
  prepare_items:
    "Wear clothes you don't mind getting a little paint on (aprons provided)\nBring your booking code — this card or the email\nCome hungry: snacks & non-alcoholic drinks are included\nBring a friend who's never been — they'll thank you\nDoors open right at your timeslot",
  terms_text:
    "By booking a workshop at MAS Arts Studio you agree to the following.\n\nBookings & payment: All sessions are booked and paid online in advance through our secure Square checkout. Your seat is only guaranteed once payment is complete — you'll receive a booking code by email and on screen. Show that code at the door.\n\nAll sales are final: We do not offer refunds for any reason. If you can't make it, text us at (920) 377-5900 at least 24 hours before your session and we'll gladly move your booking to a future workshop of equal value.\n\nWhat's included: Every ticket includes all art supplies, step-by-step hosting, snacks, and non-alcoholic drinks. No outside alcohol is served or sold by MAS Arts Studio.\n\nArrival: Doors open right at your timeslot. Arrivals more than 20 minutes late may forfeit their seat without refund, so everyone can start together.\n\nYour artwork & your clothes: Aprons are provided, but paint happens — MAS Arts Studio isn't responsible for damage to personal clothing or belongings. Fired pottery and canvases left behind for more than 30 days may be donated.\n\nPhotos: We occasionally snap photos of sessions for our social pages. Let your host know on arrival if you'd rather not be photographed — we'll always respect it.\n\nPrivacy: Your name, email, and phone number are used only to manage your booking and send your confirmation. We never sell or share your information.\n\nQuestions about any of this? Call or text (920) 377-5900 and we'll sort it out.",
};

const ContentContext = createContext({ t: (k) => DEFAULTS[k] ?? "", content: DEFAULTS });

export const useContent = () => useContext(ContentContext);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    api
      .get("/content")
      .then(({ data }) => {
        if (data && typeof data === "object") setContent({ ...DEFAULTS, ...data });
      })
      .catch(() => {});
  }, []);

  const t = useCallback((k) => content[k] ?? DEFAULTS[k] ?? "", [content]);

  return <ContentContext.Provider value={{ t, content }}>{children}</ContentContext.Provider>;
}
